'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { api, apiFetch } from '@/lib/utils/api';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { useAuthStore } from '@/stores/auth-store';

export type PlantAnomalySeverity = 'crit' | 'warn' | 'info';
export type PlantAnomalyStatusFilter = 'open' | 'resolved' | 'all';

export interface PlantAnomalyItem {
	id: string;
	plantId: string;
	plantNome: string;
	inverterId: string | null;
	inverterCodice: string | null;
	faultCode: string;
	categoria: string;
	severita: PlantAnomalySeverity;
	/** Titolo e testi arrivano dal catalogo delle tipologie, via join sull'API. */
	titolo: string;
	descrizione: string;
	soluzione: string;
	dettaglio: string | null;
	valore: number | null;
	atteso: number | null;
	occurredOn: string;
	rilevatoAt: string | null;
	risoltoAt: string | null;
}

export interface PlantAnomalyCounts {
	crit: number;
	warn: number;
	info: number;
	risolteOggi: number;
}

const EMPTY_COUNTS: PlantAnomalyCounts = { crit: 0, warn: 0, info: 0, risolteOggi: 0 };
const EMPTY_ITEMS: PlantAnomalyItem[] = [];

/**
 * Guasti d'impianto (`GET /api/plants/anomalies`), con i conteggi per gravità.
 *
 * Sottoscrive `plant_anomalies` — al contrario delle irregolarità di consumo,
 * che le scrive solo il cron notturno: qui una riga può nascere anche
 * dall'ingest della telemetria durante il giorno, e un guasto critico è
 * esattamente il tipo di cosa che non deve aspettare un ricaricamento.
 *
 * ADR-006: backed by React Query — il canale realtime invalida l'entry.
 */
export function usePlantAnomalies(
	orgId: string | null | undefined,
	opts: { status?: PlantAnomalyStatusFilter; plantId?: string | null } = {},
) {
	const { status = 'open', plantId } = opts;
	const queryClient = useQueryClient();
	const loaded = useAuthStore((s) => s.loaded);
	const enabled = loaded && !!orgId;

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: ['plant-anomalies', orgId, status, plantId ?? null],
		enabled,
		queryFn: () =>
			api.get<{ counts: PlantAnomalyCounts; items: PlantAnomalyItem[] }>(
				'/api/plants/anomalies',
				plantId
					? { orgId: orgId as string, status, plantId }
					: { orgId: orgId as string, status },
			),
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	useEffect(() => {
		if (!orgId) return;
		return subscribeToTable(
			`plant-anomalies-realtime-${orgId}`,
			{ event: '*', schema: 'public', table: 'plant_anomalies' },
			() => {
				queryClient.invalidateQueries({ queryKey: ['plant-anomalies', orgId] });
			},
		);
	}, [orgId, queryClient]);

	/** Chiude o riapre un guasto; torna true se il salvataggio è riuscito. */
	const setResolved = useCallback(
		async (id: string, resolved: boolean): Promise<boolean> => {
			if (!orgId) return false;
			const res = await apiFetch(`/api/plants/anomalies/${id}?orgId=${orgId}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ resolved }),
			});
			if (!res.ok) return false;
			await rqRefetch();
			return true;
		},
		[orgId, rqRefetch],
	);

	return {
		anomalies: Array.isArray(data?.items) ? data.items : EMPTY_ITEMS,
		counts: data?.counts ?? EMPTY_COUNTS,
		isLoading: enabled ? isLoading : false,
		error: error
			? error instanceof Error
				? error.message
				: 'Errore nel caricamento delle anomalie'
			: null,
		refetch,
		setResolved,
	};
}

export interface PlantFaultType {
	codice: string;
	categoria: string;
	severitaDefault: PlantAnomalySeverity;
	titolo: string;
	descrizione: string;
	soluzione: string;
}

const EMPTY_FAULT_TYPES: PlantFaultType[] = [];

/**
 * Il catalogo delle tipologie monitorate (`GET /api/plants/fault-types`).
 *
 * Serve al pannello informativo della pagina: quante tipologie il motore
 * riconosce è un numero che deve venire dal catalogo, non essere scritto a mano
 * nella UI — nel mock era «29», e sarebbe rimasto 29 per sempre.
 *
 * ADR-006: backed by React Query.
 */
export function usePlantFaultTypes(orgId: string | null | undefined) {
	const loaded = useAuthStore((s) => s.loaded);

	const { data } = useQuery({
		queryKey: ['plant-fault-types', orgId],
		enabled: loaded && !!orgId,
		queryFn: () => api.get<PlantFaultType[]>('/api/plants/fault-types', { orgId: orgId as string }),
	});

	return { faultTypes: Array.isArray(data) ? data : EMPTY_FAULT_TYPES };
}
